import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, CITIES } from "@/lib/api";
import { ProCard } from "@/components/ProCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { QuoteWizard } from "@/components/QuoteWizard";
import { RecommendedPros } from "@/components/RecommendedPros";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Search, Star, MapPin, BadgeCheck, Phone, Globe } from "lucide-react";

export default function Directory() {
  const nav = useNavigate();
  const [pros, setPros] = useState([]);
  const [q, setQ] = useState("");
  const [params] = useSearchParams();
  const [cat, setCat] = useState(params.get("cat") || "all");
  const [city, setCity] = useState("all");
  const [active, setActive] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quotePreset, setQuotePreset] = useState("");
  const isAgentCat = (c) => ["insurance_agent", "real_estate_agent"].includes(c);
  const [categories, setCategories] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [rev, setRev] = useState({ author: "", rating: 5, comment: "" });
  const [revLoading, setRevLoading] = useState(false);
  const cats = [{ key: "all", label: "All Services" }, ...categories.map((c) => ({ key: c.key, label: c.label }))];

  const load = () => {
    api.get("/businesses", { params: { category: cat, city, q } }).then(({ data }) => setPros(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, city]);
  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (active) { setReviews([]); api.get(`/businesses/${active.id}/reviews`).then(({ data }) => setReviews(data)).catch(() => {}); }
  }, [active]);

  const submitReview = async () => {
    if (!rev.author || !rev.comment) { toast.error("Add your name and a comment"); return; }
    setRevLoading(true);
    try {
      const { data } = await api.post(`/businesses/${active.id}/reviews`, rev);
      toast.success("Thanks for your review!");
      setReviews((p) => [data.review, ...p]);
      setActive((a) => ({ ...a, rating: data.rating, review_count: data.review_count }));
      setRev({ author: "", rating: 5, comment: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setRevLoading(false); }
  };

  return (
    <div className="container-x py-12">
      <div className="mb-8">
        <h1 className="font-display text-4xl font-extrabold text-[color:var(--navy)]">Sacramento Pro Directory</h1>
        <p className="text-[color:var(--ink-2)] mt-2">Browse verified home inspection, pest control, roofing, HVAC, contractor & specialty inspection pros serving Sacramento County + 60 miles.</p>
      </div>

      {/* Filters */}
      <div className="card-soft p-4 sm:p-5 mb-8 flex flex-col lg:flex-row lg:flex-wrap gap-4 lg:items-center">
        <div className="relative flex-1 lg:min-w-[260px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by name or service..." data-testid="directory-search-input" className="pl-9" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="directory-cat-select"
            className="h-11 rounded-lg border border-slate-300 px-3 bg-white text-sm w-full sm:w-auto sm:min-w-[220px]">
            {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="directory-city-filter"
          className="h-11 rounded-lg border border-slate-300 px-3 bg-white text-sm">
          <option value="all">All Cities</option>
          {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <Button onClick={load} data-testid="directory-search-button" className="btn-primary rounded-full px-6">Search</Button>
      </div>

      {pros.length === 0 ? (
        <div className="text-center py-20 text-[color:var(--ink-3)]" data-testid="directory-empty">No pros match your filters yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pros.map((p, i) => <ProCard key={p.id} pro={p} index={i} onView={setActive} />)}
        </div>
      )}

      {/* Detail modal */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="pro-detail-modal">
          <DialogTitle className="sr-only">Provider details</DialogTitle>
          {active && (
            <div className="min-w-0">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-[color:var(--bg-3)] overflow-hidden grid place-items-center">
                  {active.logo ? <img src={active.logo} alt={active.name} className="w-full h-full object-cover" /> : <span className="font-display font-extrabold text-2xl text-[color:var(--emerald)]">{active.name[0]}</span>}
                </div>
                <div>
                  <h3 className="font-display text-2xl font-bold text-[color:var(--navy)]">{active.name}</h3>
                  <div className="flex items-center gap-2 text-sm text-[color:var(--ink-3)] mt-1">
                    <MapPin className="w-4 h-4" /> {active.city}, CA
                    {active.verified && <span className="inline-flex items-center gap-1 badge-verified px-2 py-0.5 rounded-full text-xs"><BadgeCheck className="w-3 h-3" /> Verified</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 mt-4">
                <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                <span className="font-semibold">{active.rating.toFixed(1)}</span>
                <span className="text-sm text-[color:var(--ink-3)]">({active.review_count} reviews)</span>
              </div>
              <p className="text-[color:var(--ink-2)] mt-4">{active.description}</p>
              <div className="flex flex-wrap gap-2 mt-4">
                {active.services.map((s) => <span key={s} className="text-xs bg-[color:var(--bg-3)] px-2.5 py-1 rounded-md">{s}</span>)}
              </div>
              <div className="flex items-center gap-4 mt-5 text-sm text-[color:var(--ink-2)]">
                <span className="flex items-center gap-1"><Phone className="w-4 h-4" /> {active.phone}</span>
                {active.website && <span className="flex items-center gap-1"><Globe className="w-4 h-4" /> Website</span>}
              </div>
              <div className="mt-6 border-t border-slate-100 pt-5" data-testid="pro-reviews-section">
                <h4 className="font-display font-bold text-[color:var(--navy)] mb-3">Reviews</h4>
                {reviews.length === 0 ? (
                  <p className="text-sm text-[color:var(--ink-3)]">No reviews yet. Be the first!</p>
                ) : (
                  <div className="space-y-3 max-h-40 overflow-y-auto pr-1" data-testid="pro-reviews-list">
                    {reviews.map((r) => (
                      <div key={r.id} className="text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-[color:var(--navy)]">{r.author}</span>
                          <span className="flex items-center gap-0.5">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}</span>
                        </div>
                        <p className="text-[color:var(--ink-2)]">{r.comment}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-4 space-y-2">
                  <Input value={rev.author} onChange={(e) => setRev((p) => ({ ...p, author: e.target.value }))} placeholder="Your name" data-testid="review-author-input" />
                  <div className="flex items-center gap-1" data-testid="review-rating">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button" onClick={() => setRev((p) => ({ ...p, rating: n }))} data-testid={`review-star-${n}`}>
                        <Star className={`w-5 h-5 ${n <= rev.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                      </button>
                    ))}
                  </div>
                  <Textarea value={rev.comment} onChange={(e) => setRev((p) => ({ ...p, comment: e.target.value }))} rows={2} placeholder="Share your experience" data-testid="review-comment-input" />
                  <Button onClick={submitReview} disabled={revLoading} variant="outline" className="rounded-full" data-testid="review-submit-button">{revLoading ? "Submitting..." : "Submit Review"}</Button>
                </div>
              </div>
              {isAgentCat(active.category) && <RecommendedPros agentId={active.id}
                onQuote={(r) => { setActive(null); setQuotePreset(r.category === "both" ? "home_inspection" : r.category); setQuoteOpen(true); }} />}
              {!isAgentCat(active.category) && <Button onClick={() => { setActive(null); setQuotePreset(active.category === "both" ? "home_inspection" : active.category); setQuoteOpen(true); }} data-testid="pro-detail-request-quote"
                className="btn-amber rounded-full w-full mt-6 font-semibold">Request a Quote from {active.name.split(" ")[0]}</Button>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="directory-quote-modal">
          <DialogTitle className="sr-only">Request a quote</DialogTitle>
          <QuoteWizard key={quotePreset} presetService={quotePreset} onDone={() => setTimeout(() => setQuoteOpen(false), 3500)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
